/**
 * Récupération après un déploiement : un onglet ouvert avant la mise en
 * production référence des chunks Vite (hashés) qui n'existent plus. Le
 * `import()` dynamique échoue alors avec « Failed to fetch dynamically
 * imported module ». Plutôt que d'afficher l'erreur brute, on recharge la
 * page une seule fois pour récupérer le nouvel `index.html`.
 *
 * Garde anti-boucle : un seul rechargement par fenêtre de 10 s, mémorisé en
 * sessionStorage (accès protégé : le stockage peut être indisponible).
 */

const RELOAD_KEY = "onpatrimonia:stale-chunk-reload-at";
const RELOAD_WINDOW_MS = 10_000;

const STALE_CHUNK_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
];

export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(message));
}

function readLastReload(): number | null {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function writeLastReload(at: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(at));
  } catch {
    // Stockage indisponible : on recharge quand même, sans garde persistée.
  }
}

/**
 * Recharge la page si aucun rechargement de ce type n'a eu lieu dans les
 * 10 dernières secondes. Renvoie `true` si un rechargement est lancé.
 */
export function reloadForStaleChunk(now: number = Date.now()): boolean {
  const last = readLastReload();
  if (last !== null && now - last < RELOAD_WINDOW_MS) return false;
  writeLastReload(now);
  window.location.reload();
  return true;
}

/** Charge un module dynamique ; sur chunk périmé, recharge une fois la page. */
export async function importWithStaleChunkReload<T>(load: () => Promise<T>): Promise<T> {
  try {
    return await load();
  } catch (error) {
    if (isStaleChunkError(error) && reloadForStaleChunk()) {
      // La page se recharge : on ne résout jamais pour éviter un flash d'erreur.
      return new Promise<T>(() => {});
    }
    throw error;
  }
}
